import React, { useState } from "react";
import { RotateCcw, FileText, Building2, ArrowRight, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createFacilityLead } from "@/lib/api";

const SERVICE_NEEDED_OPTIONS = [
  "Staffing Support",
  "Multi-Patient Collections",
  "Recurring Visits",
  "Medical Courier",
  "Other",
];
const COVERAGE_OPTIONS = ["One-Time", "Ongoing", "Urgent"];

const scrollToForm = () => {
  const el = document.getElementById("request-form");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const PAY_INVOICE_MAILTO =
  "mailto:tj.clinicalsolutions@gmail.com" +
  "?subject=" + encodeURIComponent("Invoice Payment Request") +
  "&body=" + encodeURIComponent(
    "Hello T&J Clinical Solutions,\n\n" +
    "I would like to pay my existing invoice.\n\n" +
    "Name:\n" +
    "Invoice Number:\n" +
    "Amount Due:\n" +
    "Phone Number:\n" +
    "Preferred Contact Method:\n\n" +
    "Thank you."
  );

const initialFacilityForm = {
  facility_name: "",
  contact_person: "",
  title_role: "",
  email: "",
  phone: "",
  address: "",
  service_needed: "",
  coverage_needed: "",
  volume_staffing_needs: "",
  preferred_start_date: "",
  notes: "",
  preferred_laboratory: "",
  common_tests: [],
  estimated_monthly_volume: "",
};

const COMMON_TEST_OPTIONS = [
  "CBC", "CMP", "BMP", "PT/INR", "A1C", "Lipid Panel", "Drug Screen", "Other",
];

export const ReturningClient = () => (
  <section
    className="mx-auto max-w-5xl px-5 sm:px-8 py-14 sm:py-16"
    data-testid="returning-client"
  >
    <div
      className="rounded-xl border bg-white p-8 sm:p-10"
      style={{ borderColor: "#E2E8F0" }}
    >
      <div className="flex items-start gap-4">
        <span
          className="grid place-items-center h-11 w-11 rounded-lg shrink-0"
          style={{ background: "rgba(212,175,55,0.14)" }}
        >
          <RotateCcw className="h-5 w-5" style={{ color: "#D4AF37" }} strokeWidth={2.2} />
        </span>
        <div className="flex-1">
          <div
            className="text-[11px] uppercase tracking-[0.22em] font-medium"
            style={{ color: "#475569" }}
          >
            For our existing clients
          </div>
          <h2
            className="mt-1 font-display text-2xl sm:text-3xl font-bold tracking-tight"
            style={{ color: "#0B2545" }}
          >
            Returning Client?
          </h2>
          <p className="mt-2 text-sm sm:text-base" style={{ color: "#475569" }}>
            Need another appointment? Rebook quickly below.
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={scrollToForm}
          className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md text-white text-sm font-semibold tracking-wide transition-all hover:-translate-y-[1px]"
          style={{ background: "#0B2545" }}
          data-testid="rebook-now-button"
        >
          <FileText className="h-4 w-4" />
          Rebook Now
        </button>
        <a
          href={PAY_INVOICE_MAILTO}
          className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border text-sm font-semibold tracking-wide transition-all hover:-translate-y-[1px]"
          style={{ borderColor: "#0B2545", color: "#0B2545", background: "#FFFFFF" }}
          data-testid="pay-invoice-button"
        >
          Pay Existing Invoice
          <ArrowRight className="h-4 w-4" style={{ color: "#D4AF37" }} />
        </a>
      </div>
      <p
        className="mt-4 inline-flex items-center gap-2 text-xs"
        style={{ color: "#475569" }}
        data-testid="secure-payment-note"
      >
        <Mail className="h-3 w-3" style={{ color: "#D4AF37" }} />
        Opens a prefilled email — we&rsquo;ll reply with your invoice details.
      </p>
    </div>
  </section>
);

export const FacilityCallout = () => {
  const [form, setForm] = useState(initialFacilityForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.facility_name.trim()) e.facility_name = "Required.";
    if (!form.contact_person.trim()) e.contact_person = "Required.";
    if (!form.email.trim()) e.email = "Required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.phone.trim()) e.phone = "Required.";
    if (!form.service_needed) e.service_needed = "Please select one.";
    if (!form.coverage_needed) e.coverage_needed = "Please select one.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fill in the required fields.");
      return;
    }
    try {
      setSubmitting(true);
      await createFacilityLead({
        facility_name: form.facility_name,
        contact_person: form.contact_person,
        title_role: form.title_role || null,
        email: form.email,
        phone: form.phone,
        address: form.address || null,
        service_needed: form.service_needed,
        coverage_needed: form.coverage_needed,
        volume_staffing_needs: form.volume_staffing_needs || null,
        preferred_start_date: form.preferred_start_date || null,
        notes: form.notes || null,
        preferred_laboratory: form.preferred_laboratory || null,
        common_tests: form.common_tests.length ? form.common_tests : null,
        estimated_monthly_volume: form.estimated_monthly_volume || null,
      });
      setSubmitted(true);
      toast.success("Quote request received. We'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const onReset = () => {
    setForm(initialFacilityForm);
    setErrors({});
    setSubmitted(false);
  };

  return (
    <section
      className="mx-auto max-w-7xl px-5 sm:px-8 pb-16 sm:pb-20"
      data-testid="facility-callout"
    >
      <div className="overflow-hidden rounded-xl" style={{ background: "#0B2545" }}>
        <div className="px-6 sm:px-10 lg:px-12 pt-8 sm:pt-10 lg:pt-12">
          <div className="flex items-center gap-3">
            <span
              className="grid place-items-center h-10 w-10 rounded-lg shrink-0"
              style={{ background: "rgba(212,175,55,0.18)" }}
            >
              <Building2 className="h-5 w-5" style={{ color: "#D4AF37" }} strokeWidth={2.2} />
            </span>
            <span
              className="text-[11px] uppercase tracking-[0.22em] font-medium"
              style={{ color: "#E6C765" }}
            >
              For facilities & providers
            </span>
          </div>
          <h2
            className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight"
            style={{ color: "#FFFFFF" }}
          >
            For Facilities &amp; Providers
          </h2>
          <p
            className="mt-3 max-w-2xl text-sm sm:text-base leading-relaxed"
            style={{ color: "rgba(255,255,255,0.82)" }}
          >
            Reliable staffing support, recurring visits, and scalable healthcare
            solutions. Tell us about your volume and timing — we&rsquo;ll build a
            plan that fits.
          </p>
        </div>

        <div className="p-6 sm:p-10 lg:p-12 pt-6 sm:pt-8">
          {submitted ? (
            <FacilitySuccess onReset={onReset} />
          ) : (
            <form
              onSubmit={onSubmit}
              noValidate
              className="rounded-xl bg-white p-6 sm:p-8"
              data-testid="facility-form"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <FField
                  label="Facility / Company Name"
                  required
                  error={errors.facility_name}
                  testId="facility-input-name"
                >
                  <Input
                    value={form.facility_name}
                    onChange={(e) => set("facility_name", e.target.value)}
                    placeholder="Sunrise Medical Center"
                    className="h-11"
                  />
                </FField>
                <FField
                  label="Contact Person"
                  required
                  error={errors.contact_person}
                  testId="facility-input-contact"
                >
                  <Input
                    value={form.contact_person}
                    onChange={(e) => set("contact_person", e.target.value)}
                    placeholder="Jane Doe"
                    className="h-11"
                  />
                </FField>
                <FField label="Title / Role" testId="facility-input-title">
                  <Input
                    value={form.title_role}
                    onChange={(e) => set("title_role", e.target.value)}
                    placeholder="Office Manager, Director of Nursing..."
                    className="h-11"
                  />
                </FField>
                <FField label="Email" required error={errors.email} testId="facility-input-email">
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@facility.com"
                    className="h-11"
                  />
                </FField>
                <FField label="Phone" required error={errors.phone} testId="facility-input-phone">
                  <Input
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    inputMode="tel"
                    className="h-11"
                  />
                </FField>
                <div className="sm:col-span-2">
                  <FField label="Facility Address" testId="facility-input-address">
                    <Input
                      value={form.address}
                      onChange={(e) => set("address", e.target.value)}
                      placeholder="Street, City, State, ZIP"
                      className="h-11"
                    />
                  </FField>
                </div>
                <FField
                  label="Service Needed"
                  required
                  error={errors.service_needed}
                  testId="facility-input-service"
                >
                  <Select
                    value={form.service_needed}
                    onValueChange={(v) => set("service_needed", v)}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_NEEDED_OPTIONS.map((opt) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          data-testid={`facility-service-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FField>
                <FField
                  label="Coverage Needed"
                  required
                  error={errors.coverage_needed}
                  testId="facility-input-coverage"
                >
                  <Select
                    value={form.coverage_needed}
                    onValueChange={(v) => set("coverage_needed", v)}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select coverage" />
                    </SelectTrigger>
                    <SelectContent>
                      {COVERAGE_OPTIONS.map((opt) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          data-testid={`facility-coverage-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FField>
                <div className="sm:col-span-2">
                  <FField label="Volume / Staffing Needs" testId="facility-input-volume">
                    <Textarea
                      value={form.volume_staffing_needs}
                      onChange={(e) => set("volume_staffing_needs", e.target.value)}
                      placeholder="e.g. 5 phlebotomists per week, 2 couriers daily, recurring multi-patient draws on Tues/Thurs..."
                      rows={3}
                    />
                  </FField>
                </div>
                <FField label="Preferred Start Date" testId="facility-input-start-date">
                  <Input
                    type="date"
                    value={form.preferred_start_date}
                    onChange={(e) => set("preferred_start_date", e.target.value)}
                    className="h-11"
                  />
                </FField>
                <FField label="Preferred Laboratory" testId="facility-input-preferred-lab">
                  <Input
                    value={form.preferred_laboratory}
                    onChange={(e) => set("preferred_laboratory", e.target.value)}
                    placeholder="Quest, Labcorp, BioReference, in-house…"
                    className="h-11"
                  />
                </FField>
                <FField label="Estimated Monthly Volume" testId="facility-input-monthly-volume">
                  <Input
                    value={form.estimated_monthly_volume}
                    onChange={(e) => set("estimated_monthly_volume", e.target.value)}
                    placeholder="e.g. 250 draws/mo, 40 courier pickups/mo"
                    className="h-11"
                  />
                </FField>
                <div className="sm:col-span-2">
                  <FField label="Common Tests Performed" testId="facility-input-common-tests">
                    <div className="flex flex-wrap gap-2 pt-1">
                      {COMMON_TEST_OPTIONS.map((opt) => {
                        const active = form.common_tests.includes(opt);
                        return (
                          <label
                            key={opt}
                            className="text-xs px-3 py-1.5 rounded-full border cursor-pointer transition-colors"
                            style={{
                              background: active ? "#0B2545" : "#FFFFFF",
                              color: active ? "#FFD774" : "#475569",
                              borderColor: active ? "#0B2545" : "#E2E8F0",
                              fontWeight: active ? 600 : 500,
                            }}
                            data-testid={`facility-test-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          >
                            <input
                              type="checkbox"
                              checked={active}
                              onChange={() => {
                                setForm((s) => ({
                                  ...s,
                                  common_tests: active
                                    ? s.common_tests.filter((t) => t !== opt)
                                    : [...s.common_tests, opt],
                                }));
                              }}
                              className="sr-only"
                            />
                            {opt}
                          </label>
                        );
                      })}
                    </div>
                  </FField>
                </div>
                <div className="sm:col-span-2">
                  <FField label="Notes" testId="facility-input-notes">
                    <Textarea
                      value={form.notes}
                      onChange={(e) => set("notes", e.target.value)}
                      placeholder="Schedule, locations, anything else we should know..."
                      rows={4}
                    />
                  </FField>
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 px-6 rounded-md text-sm font-semibold tracking-wide transition-all hover:-translate-y-[1px] disabled:opacity-60"
                style={{ background: "#0B2545", color: "#FFFFFF" }}
                data-testid="facility-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Request Quote &mdash; We&rsquo;ll Contact You
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const FacilitySuccess = ({ onReset }) => (
  <div
    className="rounded-xl bg-white p-8 sm:p-10 text-center"
    data-testid="facility-success"
  >
    <div
      className="mx-auto grid h-14 w-14 place-items-center rounded-full"
      style={{ background: "rgba(212,175,55,0.14)" }}
    >
      <CheckCircle2 className="h-7 w-7" style={{ color: "#D4AF37" }} strokeWidth={2.2} />
    </div>
    <h3
      className="font-display mt-5 text-2xl sm:text-3xl font-bold tracking-tight"
      style={{ color: "#0B2545" }}
    >
      Quote Request Received
    </h3>
    <p className="mt-2 max-w-md mx-auto text-sm sm:text-base" style={{ color: "#475569" }}>
      Thanks for reaching out. Our partnerships team will review your details and contact you shortly with a tailored quote.
    </p>
    <button
      type="button"
      onClick={onReset}
      className="mt-5 inline-flex items-center gap-2 text-xs font-semibold underline"
      style={{ color: "#0B2545" }}
      data-testid="facility-submit-another"
    >
      Submit another request
    </button>
  </div>
);

const FField = ({ label, required, error, children, testId }) => (
  <div data-testid={testId}>
    <Label
      className="mb-1.5 flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] font-medium"
      style={{ color: "#0B2545" }}
    >
      {label}
      {required ? <span style={{ color: "#D4AF37" }}>*</span> : null}
    </Label>
    {children}
    {error ? (
      <p className="mt-1.5 text-xs font-medium" style={{ color: "#B91C1C" }}>
        {error}
      </p>
    ) : null}
  </div>
);
